import React, { useState } from "react";
import { User } from "module/types";
import { useTranslation } from "react-i18next";
import "sass/components/chat/ConversationTargetSelector.scss";

interface ConversationTargetSelectorProps {
  availablePlayers: User[];
  currentUserId: number;
  onConfirm: (selectedIds: number[]) => void;
  onCancel: () => void;
}

export const ConversationTargetSelector: React.FC<
  ConversationTargetSelectorProps
> = ({ availablePlayers, currentUserId, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<number[]>([]);

  // 자기 자신 제외
  const selectablePlayers = availablePlayers.filter(
    (player) => player.id !== currentUserId
  );

  const handlePlayerToggle = (playerId: number) => {
    setSelectedPlayerIds((prev) =>
      prev.includes(playerId)
        ? prev.filter((id) => id !== playerId)
        : [...prev, playerId]
    );
  };

  const handleConfirm = () => {
    if (selectedPlayerIds.length > 0) {
      onConfirm(selectedPlayerIds);
    }
  };

  return (
    <div className="conversation-target-selector-overlay">
      <div className="conversation-target-selector-modal">
        <div className="target-selector-header">
          <h4>{t("selectParticipants")}</h4>
          <p className="help-text">
            {selectedPlayerIds.length === 0
              ? t("selectAtLeastOne")
              : selectedPlayerIds.length === 1
              ? t("selectedOnePlayer")
              : t("selectedMultiplePlayers", {
                  count: selectedPlayerIds.length,
                })}
          </p>
        </div>

        <div className="player-list">
          {selectablePlayers.map((player) => {
            const isSelected = selectedPlayerIds.includes(player.id);
            return (
              <div
                key={player.id}
                className={`player-option ${isSelected ? "selected" : ""}`}
                onClick={() => handlePlayerToggle(player.id)}
              >
                <div className="player-info">
                  <span className="player-checkbox">
                    {isSelected ? "☑️" : "⬜"}
                  </span>
                  <span className="player-name">{player.nickName}</span>
                </div>
              </div>
            );
          })}
        </div>

        {selectablePlayers.length === 0 && (
          <div className="no-players">{t("noAvailablePlayers")}</div>
        )}

        <div className="modal-actions">
          <button className="btn-cancel" onClick={onCancel}>
            {t("cancel")}
          </button>
          <button
            className="btn-confirm"
            onClick={handleConfirm}
            disabled={selectedPlayerIds.length === 0}
          >
            {t("startConversation")}
          </button>
        </div>
      </div>
    </div>
  );
};
